import React, { useState, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Slider,
  Button,
  Avatar,
  Chip,
  Paper,
  Divider,
  Collapse,
} from '@mui/material';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SchoolIcon from '@mui/icons-material/School';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import StarIcon from '@mui/icons-material/Star';
import { useNavigate } from 'react-router-dom';

// ⚠️ Reemplaza con el número real
const WHATSAPP_NUMBER = '50300000000';

const SCHOLARSHIPS = [
  {
    min: 9.5,
    max: 10,
    percentage: 100,
    label: 'Beca Excelencia Total',
    color: '#FFD700',
    textColor: '#1a1a00',
    description: '¡Promedio excepcional! Cubres el 100% de tu matrícula. Eres exactamente el tipo de estudiante que buscamos.',
    emoji: '🏆',
    bg: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
  },
  {
    min: 8.5,
    max: 9.49,
    percentage: 75,
    label: 'Beca Académica Destacada',
    color: '#C0C0C0',
    textColor: '#fff',
    description: '¡Excelente promedio! Calificas para el 75% de descuento en tu matrícula. Un logro que reconocemos con orgullo.',
    emoji: '🥈',
    bg: 'linear-gradient(135deg, #2B2B6B 0%, #3d3d8f 100%)',
  },
  {
    min: 7.5,
    max: 8.49,
    percentage: 50,
    label: 'Beca Académica',
    color: '#00CED1',
    textColor: '#fff',
    description: 'Buen promedio. Tienes derecho a un 50% de descuento en tu matrícula. ¡Sigue esforzándote y podrías subir!',
    emoji: '🎓',
    bg: 'linear-gradient(135deg, #00CED1 0%, #009fa1 100%)',
  },
  {
    min: 6.5,
    max: 7.49,
    percentage: 25,
    label: 'Beca de Apoyo',
    color: '#E31E24',
    textColor: '#fff',
    description: 'Calificas para un 25% de descuento. Además, tenemos planes de pago flexibles para complementar tu financiamiento.',
    emoji: '📚',
    bg: 'linear-gradient(135deg, #E31E24 0%, #C41A1F 100%)',
  },
  {
    min: 0,
    max: 6.49,
    percentage: 0,
    label: 'Sin beca por promedio',
    color: '#888',
    textColor: '#fff',
    description: 'Tu promedio actual no aplica para beca académica, pero contamos con otras opciones: becas socioeconómicas, deportivas y culturales. ¡Consúltanos!',
    emoji: '💬',
    bg: 'linear-gradient(135deg, #444 0%, #222 100%)',
  },
];

const getScholarship = (avg) =>
  SCHOLARSHIPS.find((s) => avg >= s.min && avg <= s.max) || SCHOLARSHIPS[4];

const whatsappMessage = (avg, scholarship) =>
  `¡Hola! Usé la calculadora de becas y obtuve un promedio de ${avg}. Me indicó que califico para la *${scholarship.label}* (${scholarship.percentage}% de descuento). Me gustaría saber más sobre el proceso de admisión.`;

export default function ScholarshipCalculator() {
  const [average, setAverage] = useState(7.5);
  const [calculated, setCalculated] = useState(false);
  const [result, setResult] = useState(null);
  const resultRef = useRef(null);
  const navigate = useNavigate();

  const handleCalculate = () => {
    const scholarship = getScholarship(average);
    setResult(scholarship);
    setCalculated(true);
    setTimeout(() => {
      resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleReset = () => {
    setCalculated(false);
    setResult(null);
    setAverage(7.5);
  };

  const whatsappLink = result
    ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(whatsappMessage(average, result))}`
    : '#';

  return (
    <Box
      sx={{
        background: 'linear-gradient(160deg, #0D1B2A 0%, #1a2a4a 50%, #0D1B2A 100%)',
        py: { xs: 8, md: 12 },
        minHeight: '80vh',
      }}
    >
      <Container maxWidth="sm">

        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Avatar
            sx={{
              backgroundColor: '#FFD70022',
              border: '2px solid #FFD70055',
              width: 72,
              height: 72,
              mx: 'auto',
              mb: 2,
            }}
          >
            <WorkspacePremiumIcon sx={{ fontSize: 36, color: '#FFD700' }} />
          </Avatar>
          <Typography
            variant="h3"
            sx={{
              color: 'white',
              fontWeight: 800,
              fontSize: { xs: '1.8rem', md: '2.4rem' },
              mb: 1.5,
            }}
          >
            Calculadora de Becas
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.05rem' }}>
            Ingresa tu promedio de bachillerato y descubre a qué beca calificas en segundos.
          </Typography>
        </Box>

        {/* Tarjeta principal */}
        <Paper
          elevation={0}
          sx={{
            backgroundColor: '#112240',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: 4,
            p: { xs: 3, md: 5 },
            mb: 4,
          }}
        >
          {/* Promedio actual */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem', mb: 1, textTransform: 'uppercase', letterSpacing: '1px' }}>
              Tu promedio
            </Typography>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'baseline',
                gap: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: '5rem',
                  fontWeight: 900,
                  lineHeight: 1,
                  color: getScholarship(average).percentage > 0 ? '#FFD700' : 'rgba(255,255,255,0.4)',
                  transition: 'color 0.3s ease',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {average.toFixed(1)}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '1.5rem', fontWeight: 700 }}>
                / 10
              </Typography>
            </Box>

            {/* Badge de beca en tiempo real */}
            <Box sx={{ mt: 1.5 }}>
              <Chip
                icon={<StarIcon sx={{ fontSize: '16px !important', color: `${getScholarship(average).color} !important` }} />}
                label={
                  getScholarship(average).percentage > 0
                    ? `${getScholarship(average).percentage}% de descuento`
                    : 'Sin beca académica'
                }
                sx={{
                  backgroundColor: `${getScholarship(average).color}22`,
                  color: getScholarship(average).color,
                  border: `1px solid ${getScholarship(average).color}55`,
                  fontWeight: 700,
                  fontSize: '0.9rem',
                  transition: 'all 0.3s ease',
                }}
              />
            </Box>
          </Box>

          {/* Slider */}
          <Box sx={{ px: 1, mb: 4 }}>
            <Slider
              value={average}
              onChange={(_, val) => {
                setAverage(val);
                if (calculated) setCalculated(false);
              }}
              min={0}
              max={10}
              step={0.1}
              sx={{
                color: getScholarship(average).color,
                '& .MuiSlider-thumb': {
                  width: 28,
                  height: 28,
                  backgroundColor: getScholarship(average).color,
                  border: '3px solid white',
                  boxShadow: `0 0 0 4px ${getScholarship(average).color}44`,
                  transition: 'background-color 0.3s, box-shadow 0.3s',
                },
                '& .MuiSlider-track': {
                  height: 8,
                  borderRadius: 4,
                  background: `linear-gradient(90deg, #E31E24, #00CED1, #FFD700)`,
                  border: 'none',
                },
                '& .MuiSlider-rail': {
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
              }}
            />

            {/* Marcas de referencia */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              {[0, 6.5, 7.5, 8.5, 9.5, 10].map((mark) => (
                <Typography key={mark} sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.72rem' }}>
                  {mark}
                </Typography>
              ))}
            </Box>
          </Box>

          {/* Botón calcular */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleCalculate}
            endIcon={<ArrowForwardIcon />}
            sx={{
              background: 'linear-gradient(135deg, #E31E24 0%, #C41A1F 100%)',
              color: 'white',
              fontWeight: 700,
              fontSize: '1.05rem',
              py: 1.8,
              borderRadius: 2,
              textTransform: 'none',
              boxShadow: '0 8px 24px rgba(227,30,36,0.4)',
              '&:hover': {
                background: 'linear-gradient(135deg, #C41A1F 0%, #a01519 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 12px 32px rgba(227,30,36,0.5)',
              },
              transition: 'all 0.25s ease',
            }}
          >
            Calcular mi Beca
          </Button>
        </Paper>

        {/* RESULTADO */}
        <Collapse in={calculated} ref={resultRef}>
          {result && (
            <Paper
              elevation={0}
              sx={{
                background: result.bg,
                borderRadius: 4,
                p: { xs: 3, md: 5 },
                textAlign: 'center',
                border: `2px solid ${result.color}66`,
                boxShadow: `0 24px 64px ${result.color}33`,
                animation: 'popIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                '@keyframes popIn': {
                  '0%': { opacity: 0, transform: 'scale(0.85)' },
                  '100%': { opacity: 1, transform: 'scale(1)' },
                },
              }}
            >
              {/* Emoji grande */}
              <Typography sx={{ fontSize: '4rem', mb: 1, lineHeight: 1 }}>
                {result.emoji}
              </Typography>

              {result.percentage > 0 ? (
                <>
                  <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '1.5px', mb: 1 }}>
                    ¡Felicidades! Calificas para
                  </Typography>
                  <Typography
                    variant="h4"
                    sx={{
                      color: result.percentage === 100 ? result.textColor : 'white',
                      fontWeight: 900,
                      fontSize: { xs: '1.6rem', md: '2rem' },
                      mb: 1,
                    }}
                  >
                    {result.label}
                  </Typography>
                  <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5, mb: 2 }}>
                    <Typography sx={{ color: result.percentage === 100 ? result.textColor : 'white', fontSize: '4.5rem', fontWeight: 900, lineHeight: 1 }}>
                      {result.percentage}%
                    </Typography>
                    <Typography sx={{ color: result.percentage === 100 ? result.textColor : 'rgba(255,255,255,0.7)', fontSize: '1.2rem', fontWeight: 600 }}>
                      de descuento
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <Typography variant="h5" sx={{ color: 'white', fontWeight: 700, mb: 1 }}>
                    {result.label}
                  </Typography>
                </>
              )}

              <Typography
                sx={{
                  color: result.percentage === 100 ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.8)',
                  fontSize: '1rem',
                  lineHeight: 1.7,
                  mb: 3,
                  px: 1,
                }}
              >
                {result.description}
              </Typography>

              <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', mb: 3 }} />

              {/* Acciones */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  startIcon={<WhatsAppIcon />}
                  sx={{
                    backgroundColor: '#25D366',
                    color: 'white',
                    fontWeight: 700,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    boxShadow: '0 6px 20px rgba(37,211,102,0.4)',
                    '&:hover': { backgroundColor: '#1ebe5d' },
                  }}
                >
                  Confirmar mi beca por WhatsApp
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<SchoolIcon />}
                  onClick={() => navigate('/admissions')}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.4)',
                    color: 'white',
                    fontWeight: 600,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontSize: '0.95rem',
                    '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.1)' },
                  }}
                >
                  Ver proceso de admisión
                </Button>

                <Button
                  onClick={handleReset}
                  sx={{
                    color: 'rgba(255,255,255,0.5)',
                    textTransform: 'none',
                    fontSize: '0.85rem',
                    '&:hover': { color: 'white' },
                  }}
                >
                  Calcular de nuevo
                </Button>
              </Box>
            </Paper>
          )}
        </Collapse>

        {/* Nota aclaratoria */}
        <Typography
          sx={{
            color: 'rgba(255,255,255,0.25)',
            fontSize: '0.78rem',
            textAlign: 'center',
            mt: 4,
            px: 2,
          }}
        >
          * Esta calculadora es orientativa. La beca definitiva se confirma tras verificar
          tu expediente académico con el equipo de admisiones.
        </Typography>

      </Container>
    </Box>
  );
}